import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { formatElapsedSince } from '@/lib/format';
import { notificationsToHomeFeedItems, type HomeFeedItem, type NotificationItem } from '@/lib/home-feed';

/**
 * v0.4 D-U10 (Phase1): ホーム画面のお知らせ欄。
 * 既存の通知基盤 (`GET /notifications?unread=1`) の未読分をホームにも一覧表示する。
 * データ取得のクエリキーは NotificationBell.tsx と同一にしてあり (同じ QueryClient 配下では
 * TanStack Query がキャッシュを共有するため)、ヘッダーの通知ベルと二重にフェッチしない。
 *
 * `HomeAnnouncementsList` (表示専用、HomeFeedItem[] のみに依存) と `HomeAnnouncements`
 * (データ取得・既読化を担うコンテナ) に分離してある。Phase2 (掲示板) を追加する際は
 * コンテナ側で bulletin 由来の HomeFeedItem を notification 由来のものと合成して
 * HomeAnnouncementsList へ渡すだけで済む設計 (表示側の変更は不要)。
 */

type NotificationsResponse = { notifications: NotificationItem[]; unread_count: number };

const DISPLAY_LIMIT = 5;
const POLL_INTERVAL_MS = 45_000;

export function HomeAnnouncementsList({
  items,
  onItemClick,
  onViewAll,
}: {
  items: HomeFeedItem[];
  onItemClick: (item: HomeFeedItem) => void;
  onViewAll: () => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-section text-text-section">お知らせ</h2>
        <div className="flex-1 h-px bg-surface-line" aria-hidden />
      </div>
      <div className="rounded-lg bg-surface-card shadow-card overflow-hidden">
        {/* このセクションは未読分のみを取得するため (D-U10)、行ごとの未読ドットは常に全件点灯し
            冗長になる。赤は「すべて見る」1箇所のみに絞る (design-expert指摘、ui-guidelines§2.2)。 */}
        <ul className="divide-y divide-surface-line">
          {items.map((item) => (
            <li key={`${item.kind}:${item.id}`}>
              <button
                type="button"
                onClick={() => onItemClick(item)}
                className="w-full text-left px-4 py-3 min-h-[44px] hover:bg-surface-sunken"
              >
                <span className="block text-sm font-semibold text-text-body break-words">{item.title}</span>
                <span className="block text-xs text-text-sub break-words line-clamp-2 mt-0.5">{item.body}</span>
                <span className="block text-[11px] text-text-muted mt-1">{formatElapsedSince(item.createdAt)}</span>
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onViewAll}
          className="w-full text-center text-sm text-brand-red font-semibold hover:underline min-h-[44px] px-4 py-2.5 bg-surface-sunken border-t border-surface-line"
        >
          すべて見る
        </button>
      </div>
    </section>
  );
}

export function HomeAnnouncements() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ['notifications', { unread: true }],
    queryFn: () => apiFetch<NotificationsResponse>('/notifications?unread=1'),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const readMut = useMutation({
    mutationFn: (notificationId: string) => apiFetch(`/notifications/${notificationId}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = notificationsToHomeFeedItems(data?.notifications ?? []).slice(0, DISPLAY_LIMIT);

  return (
    <HomeAnnouncementsList
      items={items}
      onItemClick={(item) => readMut.mutate(item.id)}
      onViewAll={() => navigate('/notifications')}
    />
  );
}
